import { useState, useEffect } from "react";
import { bg1, bg2, bg3, bg6, bg7, bg8 } from "../../../assets";

const desktopSlides = [
  { src: bg1, alt: "Sports Complex Field" },
  { src: bg2, alt: "Sports Complex Courts" },
  { src: bg3, alt: "Sports Complex Facilities" },
];

const mobileSlides = [
  { src: bg6, alt: "Sports Complex Field" },
  { src: bg7, alt: "Sports Complex Courts" },
  { src: bg8, alt: "Sports Complex Facilities" },
];

// Kick off the fetch for the FIRST hero slide the instant this module runs
// - before React has mounted anything. This is the earliest a fully
// client-rendered app can start the request for its LCP image, since there's
// no server-rendered <img> tag in the initial HTML for the browser's preload
// scanner to discover on its own.
if (typeof window !== "undefined" && typeof document !== "undefined") {
  const heroSrc = window.innerWidth < 768 ? bg6 : bg1;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = heroSrc;
  document.head.appendChild(link);
}

const Section1 = () => {
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => {
      setIsMobile(e.matches);
      setCurrent(0);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const slides = isMobile ? mobileSlides : desktopSlides;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides]);

  const scrollToPromotions = () => {
    const section = document.getElementById("promotions");

    if (window.__lenis__) {
      window.__lenis__.scrollTo(section, {
        duration: 1.4,
        offset: 0,
      });
    } else {
      section?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden flex">
      <div className="absolute inset-0 bg-black" />

      {/* Image container */}
      <div className="absolute left-0 top-0 h-full w-full md:w-[65%]">
        <div
          className="relative w-full h-full overflow-hidden"
          style={
            !isMobile
              ? {
                  borderRadius: "0 15% 15% 0 / 0 50% 50% 0",
                  borderRight: "10px solid rgba(0,0,0,0.15)",
                }
              : {}
          }
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === current ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "low"}
                decoding="async"
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}

          {/* Desktop: right-edge fade */}
          <div
            className="absolute inset-0 pointer-events-none hidden md:block"
            style={{
              background:
                "linear-gradient(to right, transparent 45%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.92) 88%, #000 100%)",
            }}
          />

          {/* Mobile: vignette */}
          <div
            className="absolute inset-0 pointer-events-none md:hidden"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%)",
            }}
          />

          {/* Top/bottom vignette */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.35) 100%)",
            }}
          />
        </div>

        {/* Slide indicators - hidden on mobile per design request, shown on desktop */}
        <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 md:left-12 md:translate-x-0 z-10 gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className={`h-1 rounded-full transition-all duration-500 ${
                index === current ? "w-8 bg-red-600" : "w-2 bg-white/30"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Desktop text panel */}
      <div className="hidden md:flex absolute bg-white/0 right-0 top-0 h-full w-[35%] flex-col justify-center px-10 lg:px-16 xl:px-20">
        <p className="text-xs uppercase tracking-[0.35em] text-red-600 font-semibold mb-5">
          More Than a Venue
        </p>
        <h1 className="text-5xl lg:text-6xl xl:text-7xl font-black text-white uppercase leading-none tracking-tight">
          A Sporting
          <br />
          <span className="text-red-600">Experience</span>
        </h1>
        <div className="mt-6 mb-6 w-12 h-0.5 bg-red-700" />
        <p className="text-sm lg:text-base text-white/55 max-w-xs leading-relaxed">
          World-class indoor sports facilities in Godagama, minutes from Homagama, Meegoda and Colombo - for every athlete, every level, every goal.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <button 
            onClick={() => window.location.href = "/booking"}
            className="px-8 py-3 bg-red-700 hover:bg-red-600 text-white font-bold uppercase tracking-widest text-xs transition-colors duration-200">
            Book a Facility
          </button>
          <button
            onClick={scrollToPromotions}
            className="px-8 py-3 border border-white/20 hover:border-red-600 hover:text-red-500 text-white/60 font-bold uppercase tracking-widest text-xs transition-colors duration-200"
          >
            View Promotions
          </button>
        </div>
        <p className="mt-12 text-white/20 text-xs font-mono tracking-widest">
          {String(current + 1).padStart(2, "0")} /{" "}
          {String(slides.length).padStart(2, "0")}
        </p>
      </div>

      {/* Mobile text overlay */}
      <div
        className="md:hidden absolute inset-0 z-10 flex flex-col justify-end pb-16 px-8"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.85) 40%, transparent 100%)",
        }}
      >
        <p className="text-xs uppercase tracking-[0.35em] text-red-600 font-semibold mb-4">
          More Than a Venue
        </p>
        <h1 className="text-5xl font-black text-white uppercase leading-none tracking-tight mb-2">
          A Sporting
          <br />
          <span className="text-red-600">Experience</span>
        </h1>
        <div className="my-4 w-10 h-0.5 bg-red-700" />
        <p className="text-sm text-white/55 max-w-xs leading-relaxed mb-8">
          World-class facilities for every athlete, every level, every goal.
        </p>
        <div className="flex gap-3">
          <button 
            onClick={() => window.location.href = "/booking"}
            className="px-6 py-3 bg-red-700 hover:bg-red-600 text-white font-bold uppercase tracking-widest text-xs transition-colors">
              Book a Facility
          </button>
          <button
            onClick={scrollToPromotions}
            className="px-6 py-3 border border-white/20 text-white/60 font-bold uppercase tracking-widest text-xs"
          >
            View Promotions
          </button>
        </div>
      </div>
    </div>
  );
};

export default Section1;
